import { useEffect, useMemo, useRef, useState } from 'react'
import { useSaveSkillFile, useSkillFile } from '../../lib/queries/systems.js'
import { formatRelative } from '../../lib/dates.js'
import { Icon } from '../icons/Icon.jsx'
import l from './library.module.css'

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`
}

/**
 * Right-side drawer for one skill file: a source view with a section outline
 * (deep-linkable via #anchor), or an editor that saves into the draft with
 * optimistic concurrency (baseSha).
 *
 * mode: "view" | "edit" | "new"
 */
export function SkillFileDrawer({ system, version, path, mode, canEdit, onClose, onEdit, onSaved }) {
  const isNew = mode === 'new'
  const file = useSkillFile(system.slug, version, isNew ? null : path)
  const [hash, setHash] = useState(() => window.location.hash.slice(1))

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const title = isNew ? 'New skill file' : path
  const meta = file.data
    ? [file.data.file.sectionLabel, formatBytes(file.data.file.bytes), `updated ${formatRelative(file.data.file.updatedAt)}`]
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <div
      className={l.drawerBackdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section className={l.drawer} role="dialog" aria-modal="true" aria-label={title}>
        <div className={l.drawerHead}>
          <Icon name={path?.endsWith('.json') ? 'braces' : 'file'} size={16} strokeWidth={1.5} stroke="#5E626A" />
          <span className={l.drawerTitle}>
            <span className={l.drawerPath}>{title}</span>
            <span className={l.drawerMeta}>
              {system.name} · {version === 'draft' ? 'draft' : `skill v${system.published?.label ?? ''}`}
              {meta ? ` · ${meta}` : ''}
            </span>
          </span>
          {mode === 'view' && canEdit ? (
            <button type="button" className="btn btn-ghost" onClick={onEdit}>
              <Icon name="edit" size={14} strokeWidth={1.6} />
              Edit
            </button>
          ) : null}
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={14} strokeWidth={1.7} />
          </button>
        </div>
        {mode === 'view' ? (
          <FileView file={file} hash={hash} onNavigate={setHash} />
        ) : (
          <FileEditor
            key={`${path}-${file.data?.file.sha256 ?? 'new'}`}
            system={system}
            isNew={isNew}
            path={path}
            file={file}
            onCancel={onClose}
            onSaved={onSaved}
          />
        )}
      </section>
    </div>
  )
}

function FileView({ file, hash, onNavigate }) {
  const sourceRef = useRef(null)
  const lines = useMemo(() => (file.data?.content ?? '').split('\n'), [file.data?.content])
  const anchorsByLine = useMemo(
    () => new Map((file.data?.sections ?? []).map((section) => [section.line, section])),
    [file.data?.sections],
  )

  useEffect(() => {
    if (!hash || !file.data) return
    const target = document.getElementById(hash)
    target?.scrollIntoView({ block: 'start' })
  }, [hash, file.data])

  if (file.isPending) {
    return (
      <div className={l.drawerBody}>
        <div className={l.source}>
          {[90, 70, 84, 60, 76].map((w, i) => (
            <div key={i} className="skeleton" style={{ height: 12, width: `${w}%`, marginBottom: 12 }} />
          ))}
        </div>
      </div>
    )
  }
  if (file.isError) {
    return (
      <div className={l.drawerBody}>
        <p className={l.source}>This file could not be loaded.</p>
      </div>
    )
  }
  const sections = (file.data.sections ?? []).filter((s) => s.level <= 3)
  return (
    <div className={l.drawerBody}>
      {sections.length ? (
        <nav className={l.toc} aria-label="Sections">
          {sections.map((section) => (
            <a
              key={section.anchor}
              href={`#${section.anchor}`}
              className={`${l.tocLink} ${hash === section.anchor ? l.tocActive : ''}`}
              style={{ paddingLeft: 8 + (section.level - 1) * 8 }}
              onClick={(event) => {
                event.preventDefault()
                window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${section.anchor}`)
                onNavigate(section.anchor)
              }}
            >
              {section.num ? `§${section.num} ` : ''}
              {section.title}
            </a>
          ))}
        </nav>
      ) : null}
      <div className={l.source} ref={sourceRef}>
        {lines.map((line, i) => {
          const section = anchorsByLine.get(i + 1)
          return section ? (
            <div key={i} id={section.anchor} className={`${l.headingLine} ${hash === section.anchor ? l.headingTarget : ''}`}>
              {line}
            </div>
          ) : (
            <div key={i}>{line || ' '}</div>
          )
        })}
      </div>
    </div>
  )
}

function FileEditor({ system, isNew, path, file, onCancel, onSaved }) {
  const save = useSaveSkillFile(system.slug)
  const [newPath, setNewPath] = useState(isNew ? 'references/' : path)
  const [content, setContent] = useState(file.data?.content ?? '')
  const [summary, setSummary] = useState(file.data?.file.summary ?? '')
  const baseSha = useRef(isNew ? null : (file.data?.file.sha256 ?? undefined))
  const dirty = content !== (file.data?.content ?? '') || summary !== (file.data?.file.summary ?? '') || isNew

  const submit = () => {
    const target = (isNew ? newPath : path).trim()
    if (!target) return
    save.mutate(
      { path: target, content, summary: summary.trim() || null, baseSha: baseSha.current },
      { onSuccess: ({ file: saved }) => onSaved?.(saved.path) },
    )
  }

  if (!isNew && file.isError) {
    return (
      <div className={l.editor}>
        <p>This file could not be loaded. Close the drawer and try again.</p>
      </div>
    )
  }

  if (!isNew && file.isPending) {
    return (
      <div className={l.editor}>
        <div className="skeleton" style={{ flex: 1, borderRadius: 10 }} />
      </div>
    )
  }

  return (
    <form
      className={l.editor}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      onKeyDown={(event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 's') {
          event.preventDefault()
          submit()
        }
      }}
    >
      {isNew ? (
        <label className="field">
          <span>Path</span>
          <input value={newPath} onChange={(e) => setNewPath(e.target.value)} placeholder="references/new-file.md" autoFocus />
          <small>SKILL.md is the primary file; put references in references/ and JSON in assets/.</small>
        </label>
      ) : null}
      <label className="field">
        <span>Description in the library</span>
        <input value={summary} maxLength={160} onChange={(e) => setSummary(e.target.value)} placeholder="What this file covers" />
      </label>
      <textarea
        className={l.editorArea}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        spellCheck={false}
        aria-label="File content"
        autoFocus={!isNew}
      />
      <div className={l.editorFoot}>
        <span>
          Saves into the draft. Answers keep using the published version until you publish.
          {save.isError && save.error?.status === 409 ? ' Reload the file to get the latest version.' : ''}
        </span>
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Close
          </button>
          <button type="submit" className="btn btn-dark" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save to draft'}
          </button>
        </span>
      </div>
    </form>
  )
}
